import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { TextInput } from '../../FormUtils/TextInput'
import { useMemo, useState } from 'react'
import Select from '../../FormUtils/Select'
import useApiService from '../../../services/api'
import { toast } from 'react-toastify'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { ChevronDown, ChevronUp } from 'lucide-react'
import {
  checkDoorSelectionRules,
  selectDoorsStepFormSchema,
} from '../CreateDealModal/SelectDoorsStep'
import { useProductVariantSearch } from 'components/UI/ProductSearchModal'
import { MobileModal } from 'components/UI/Modal/MobileModal'
import FbButton from 'components/FbUI/FbButton'
import { RadioIconSelect } from 'components/FormUtils/RadioIconSelect'
import { Building2, MapPin } from 'lucide-react'
import { FbLink } from 'components/FbUI/FbLink'
import { MobileSelectLocationsModal } from './MobileSelectLocationsModal'
import { MobileConfirmationModal } from 'components/MobileConfirmationModal'
import { useQueryClient } from '@tanstack/react-query'
import { Spinner } from 'react-bootstrap'
import { DealDetail } from 'models/deals'

interface EditDealModalProps {
  show: boolean
  onClose: () => void
  dealId?: number
  dealsTableQueryKey: string[]
}

const editDealSchema = z
  .object({
    name: z
      .union([
        z.string().max(100, 'Deal name must be 100 characters or less'),
        z.null(),
        z.undefined(),
      ])
      .optional()
      .nullable(),
    monthly_volume_override: z.union([
      z.number().min(0).optional().nullable(),
      z.literal(''),
    ]),
    monthly_revenue_override: z.union([
      z.number().min(0).optional().nullable(),
      z.literal(''),
    ]),
    close_date_override: z.string().optional().nullable(),
    account_owner: z.number().optional().nullable(),
    sales_stage: z.number().optional().nullable(),
    product_variant_id: z.number().optional().nullable(),
    notes: z.string().optional().nullable(),
    attachments: z.array(z.any()).optional().nullable(),
    origin_campaign_id: z.number().optional().nullable(),
    product_id: z.number(),
  })
  .merge(selectDoorsStepFormSchema)
  .superRefine((v, ctx) => {
    checkDoorSelectionRules(v, ctx)
  })
type EditDealFormValues = z.infer<typeof editDealSchema>

// ---------------------------------------------------------------------------
// Shell: fetches all data, shows spinner until ready, then renders form content
// ---------------------------------------------------------------------------

export function MobileEditDealModal(props: EditDealModalProps) {
  const api = useApiService()

  // Fetch fresh deal data when modal opens
  const { data: deal, isLoading: isDealLoading } = api.useGetDeal(
    props.dealId,
    props.show,
    { noCache: true }
  )

  const { data: stageOptions, isLoading: isStageOptionsLoading } =
    api.useGetSalesStageOptions()
  const { data: userOptions, isLoading: isUserOptionsLoading } =
    api.useGetUserOptions({ onlyActive: true })
  const { data: productOptions, isLoading: isProductOptionsLoading } =
    api.useGetProductsOptions()

  const { data: campaignOptions, isLoading: isCampaignOptionsLoading } =
    api.useGetCampaignsOptions({
      typeOrTypeId: deal?.contact_company?.sub_category?.id ?? undefined,
      requireFilter: true,
    })

  const { isLoading: isProductsLoading } = api.useGetProducts()

  // Fetch door IDs via React Query (only when deal uses select_locations)
  const shouldFetchDoorIds =
    props.show && !!deal && deal.apply_to === 'select_locations'
  const { data: doorIdsData, isLoading: isDoorIdsLoading } =
    api.useGetSelectedDoorIdsForDeal(deal?.id, shouldFetchDoorIds, {
      noCache: true,
    })

  const isReady = useMemo(() => {
    if (isDealLoading) return false
    if (
      isStageOptionsLoading ||
      isUserOptionsLoading ||
      isProductOptionsLoading ||
      isCampaignOptionsLoading ||
      isProductsLoading
    )
      return false
    if (shouldFetchDoorIds && isDoorIdsLoading) return false
    if (!deal) return false
    return true
  }, [
    isDealLoading,
    isStageOptionsLoading,
    isUserOptionsLoading,
    isProductOptionsLoading,
    isCampaignOptionsLoading,
    isProductsLoading,
    shouldFetchDoorIds,
    isDoorIdsLoading,
    deal,
  ])

  // Compute initial door IDs for the form
  const initialDoorIds = useMemo(() => {
    if (deal?.apply_to === 'select_locations') {
      return doorIdsData?.ids ?? []
    }
    return []
  }, [deal?.apply_to, doorIdsData?.ids])

  // Don't render anything when modal is closed
  if (!props.show) return null

  // Show loading modal until all data is ready
  if (!isReady) {
    return (
      <MobileModal
        header={
          <div className="p-2">
            <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
              Edit A Deal
            </div>
            <h1 className="text-2xl font-semibold text-gray-900">Loading...</h1>
          </div>
        }
        footer={<div />}
      >
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-2">
            <Spinner animation="border" />
            <span className="text-gray-600 text-sm">Loading deal data...</span>
          </div>
        </div>
      </MobileModal>
    )
  }

  // All data ready - render form content
  return (
    <MobileEditDealFormContent
      key={deal!.id}
      deal={deal!}
      initialDoorIds={initialDoorIds}
      stageOptions={stageOptions}
      userOptions={userOptions}
      productOptions={productOptions}
      campaignOptions={campaignOptions}
      dealsTableQueryKey={props.dealsTableQueryKey}
      onClose={props.onClose}
    />
  )
}

// ---------------------------------------------------------------------------
// Form content: receives ready data, initializes form directly, no sync effects
// ---------------------------------------------------------------------------

interface MobileEditDealFormContentProps {
  deal: DealDetail
  initialDoorIds: number[]
  stageOptions: any
  userOptions: any
  productOptions: any
  campaignOptions: any
  dealsTableQueryKey: string[]
  onClose: () => void
}

function MobileEditDealFormContent({
  deal,
  initialDoorIds,
  stageOptions,
  userOptions,
  productOptions,
  campaignOptions,
  dealsTableQueryKey,
  onClose,
}: MobileEditDealFormContentProps) {
  const queryClient = useQueryClient()
  const featureFlag = useFeatureFlagService()
  const api = useApiService()

  const [overridesExpanded, setOverridesExpanded] = useState(false)
  const [showLocationModal, setShowLocationModal] = useState(false)
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false)

  // Form initialized directly with all ready data - no sync useEffects needed
  const form = useForm<EditDealFormValues>({
    resolver: zodResolver(editDealSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
    defaultValues: {
      name: deal.name || '',
      product_id: deal.product_id ?? 0,
      product_variant_id: deal.product_variant_id,
      monthly_volume_override: deal.monthly_volume_override,
      monthly_revenue_override: deal.monthly_revenue_override,
      close_date_override: deal.close_date_override,
      account_owner: deal.account_owner,
      sales_stage: deal.sales_stage,
      notes: '',
      attachments: [],
      origin_campaign_id: deal.origin_campaign,
      apply_to: deal.apply_to,
      door_ids: initialDoorIds,
    },
  })

  const search = useProductVariantSearch({
    mode: 'variants',
    initialFilters: form?.watch('product_id')
      ? { productId: form?.watch('product_id') }
      : undefined,
    pageSize: 50,
    enabled: true,
  })

  const { setValue, watch } = form

  const onSubmit = form.handleSubmit(async (values) => {
    const body = {
      name: values.name || undefined,
      product_id: values.product_id,
      product_variant_id: values.product_variant_id ?? undefined,
      monthly_volume_override: values.monthly_volume_override || null,
      monthly_revenue_override: values.monthly_revenue_override || null,
      close_date_override: values.close_date_override ?? undefined,
      account_owner: values.account_owner ?? undefined,
      sales_stage: values.sales_stage ?? undefined,
      notes: values.notes ?? undefined,
      attachments: values.attachments ?? undefined,
      origin_campaign_id: values.origin_campaign_id
        ? values.origin_campaign_id.toString()
        : null,
      apply_to: values.apply_to,
      door_ids: values.door_ids,
    }

    await api.editDeal(deal.id, body)
    toast.success('Deal updated successfully')

    onClose()

    await new Promise((resolve) => setTimeout(resolve, 1000))
    await queryClient.refetchQueries({ queryKey: dealsTableQueryKey })
  })

  const doorIds = watch('door_ids')

  // Location modal view
  if (showLocationModal && deal.contact_company?.id) {
    return (
      <MobileSelectLocationsModal
        initialDoorIds={doorIds ?? initialDoorIds}
        chainProxyId={deal.contact_company.id}
        onCancel={() => {
          setShowLocationModal(false)
        }}
        onSubmit={(selectedDoorIds: number[]) => {
          setValue('door_ids', selectedDoorIds)
          setShowLocationModal(false)
        }}
      />
    )
  }

  // Delete confirmation view
  if (showDeleteConfirmationModal) {
    return (
      <MobileConfirmationModal
        title="Delete this Deal?"
        message="This action cannot be undone."
        onClose={() => setShowDeleteConfirmationModal(false)}
        onConfirm={async () => {
          await api.deleteDeal(deal.id)
          onClose()
          queryClient.invalidateQueries()
          setShowDeleteConfirmationModal(false)
        }}
      />
    )
  }

  return (
    <MobileModal
      header={
        <div className="p-2">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
            Edit A Deal
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">{deal.name}</h1>
          <div className="text-xs text-fb-green-800 mt-2">
            <FbLink to={`${deal.contact_company?.id}`}>
              {deal.contact_company?.name}
            </FbLink>
          </div>
        </div>
      }
      footer={
        <div>
          <div className="flex gap-4 p-2 justify-between">
            <div className="w-1/2">
              <FbButton
                className="w-full"
                type="button"
                size="mdlg"
                variant="secondary"
                onClick={onClose}
              >
                Cancel
              </FbButton>
            </div>
            <div className="w-1/2">
              <FbButton
                className="w-full"
                type="button"
                size="mdlg"
                variant="primary"
                onClick={onSubmit}
              >
                Save
              </FbButton>
            </div>
          </div>
          <div className="flex justify-center">
            <FbLink
              onClick={(e) => {
                e.preventDefault()
                setShowDeleteConfirmationModal(true)
              }}
              style={{ fontSize: 10 }}
              className="text-danger"
              to="#"
            >
              Delete Deal
            </FbLink>
          </div>
        </div>
      }
    >
      <div className="space-y-6 w-full p-2">
        <div>
          <TextInput
            control={form.control}
            name="name"
            label="Deal Name"
            placeholder={deal.contact_company?.name || 'Company Name'}
            optional={true}
            maxLength={100}
          />
        </div>

        <div>
          <Select
            label={'Product'}
            control={form.control}
            name={`product_id`}
            options={productOptions}
            placeholder="Select..."
            className="mt-1"
            isClearable={false}
          />
        </div>

        <div>
          <Select
            label={'Product Variant'}
            control={form.control}
            name={`product_variant_id`}
            options={search.variants?.map((v) => ({
              value: v?.id,
              label: v?.name,
            }))}
            placeholder="Select Variant"
            className="mt-1"
            disabled={!form.watch('product_id')}
            optional
          />
        </div>

        {/* Sales Stage, Account Owner, and Origin Campaign Row */}
        <div className="flex flex-col gap-2">
          <Select
            label={'Sales Stage'}
            control={form.control}
            name={`sales_stage`}
            options={stageOptions}
            placeholder="Target"
            className="mt-1"
          />
          <Select
            label={'Account Owner'}
            control={form.control}
            name={`account_owner`}
            options={userOptions}
            placeholder="Select..."
            className="mt-1"
          />
          {featureFlag.enableCampaigns ? (
            <Select
              label={'Origin Campaign'}
              control={form.control}
              name={`origin_campaign_id`}
              options={campaignOptions}
              placeholder="Select..."
              className="mt-1"
            />
          ) : (
            <div className="h-0" />
          )}
        </div>

        {/* Deal Overrides Section - Collapsible */}
        <div className="bg-surface-muted rounded-lg p-4 mb-4">
          <button
            type="button"
            onClick={() => setOverridesExpanded(!overridesExpanded)}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-medium text-gray-900">
                Deal Overrides
              </h4>
              <span className="bg-sage-lt text-gray-500 text-xs px-2 py-1 rounded-full">
                Optional
              </span>
            </div>
            {overridesExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-500" />
            )}
          </button>

          {overridesExpanded && (
            <div className="mt-4 space-y-4">
              <TextInput
                control={form.control}
                label={'Est. Monthly Volume'}
                name={`monthly_volume_override`}
                type="number"
                placeholder="2000"
                optional={true}
              />
              <TextInput
                control={form.control}
                label={'Est. Monthly Revenue'}
                name={`monthly_revenue_override`}
                type="number"
                placeholder="$30,000"
                optional={true}
                step="0.01"
              />
              <TextInput
                control={form.control}
                name={`close_date_override`}
                label={'Est. Close Date'}
                type="date"
                optional={true}
              />
            </div>
          )}
        </div>

        {/* Door selection */}
        <div className="border border-gray-400 rounded-md p-3 flex flex-col gap-2">
          <div>Apply this deal to:</div>
          <RadioIconSelect
            name="apply_to"
            control={form.control}
            options={[
              {
                label: 'Entire Company',
                key: 'entire_company',
                icon: Building2,
              },
              {
                label: doorIds?.length
                  ? `Select Locations (${doorIds.length})`
                  : 'Select Locations',
                key: 'select_locations',
                icon: MapPin,
                onClick: () => setShowLocationModal(true),
              },
            ]}
          />
        </div>
      </div>
    </MobileModal>
  )
}
